import XUILoader from '@xero/xui/react/loader';
import { XUIPanel, XUIPanelSection } from '@xero/xui/react/panel';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import logos from '../../../assets/logos';
import GroupedCheckbox, { IGroupedCheckboxItem } from '../../../components/GroupedCheckbox';
import MultiselectPicklist, { IMultiselectPicklistItem } from '../../../components/MultiselectPicklist';
import SetupSegment from '../../../components/SetupSegment';
import { IContactType, IHubspotSettings, IHubspotSettingsResponse, ISmartList } from '../../../models';
import { installationSelectors } from '../../../redux/slices/InstallationSlice';
import { settingsSelectors, updateSettingsSuccess } from '../../../redux/slices/SettingsSlice';
import { useAppDispatch } from '../../../redux/store';
import { contactTypes } from '../../../static/constants';
import lang from '../../../static/lang/en-NZ';
import { NavPanelSection } from '../Common';
import useDidMountEffect from '../../../helpers/hooks';

export interface IHubspotSetupPanelProps {
    isSaving: boolean;
    onSaveClick: (settings: Partial<IHubspotSettings>, isFinal?: boolean) => void;
    onCancelClick: () => void;
}

const getContactTypeItems = (contactSyncSettings: IContactType[]): IMultiselectPicklistItem[] =>
    (contactSyncSettings || []).map((contactType) => ({
        id: contactType.type.toUpperCase(),
        name: `${contactType.type.toUpperCase() === contactTypes.OTHER ? contactType.type : contactType.type + 's'}`,
        selected: contactType.isSyncEnabled,
    }));

const getSmartListItems = (smartLists: ISmartList[]): IGroupedCheckboxItem[] =>
    (smartLists || []).map((smartList) => ({
        id: smartList.id,
        name: smartList.name,
        selected: smartList.selected,
    }));

const HubspotSetupPanel: FC<IHubspotSetupPanelProps> = (props) => {
    const dispatch = useAppDispatch();

    const installation = useSelector(installationSelectors.installation);
    const settings = useSelector(settingsSelectors.settings) as IHubspotSettingsResponse;
    const isFetchingSettings = useSelector(settingsSelectors.isFetching);

    const [smartLists, setSmartLists] = useState<IGroupedCheckboxItem[]>([]);
    const [contactTypeItems, setContactTypeItems] = useState<IMultiselectPicklistItem[]>([]);

    const integrationLang: any = lang[installation.integrationType];

    useEffect(() => {
        if (isFetchingSettings) {
            return;
        }
        setContactTypeItems(getContactTypeItems(settings.contactSyncSettings));
        setSmartLists(getSmartListItems(settings.smartLists));
        // All dependencies used in a hook should be injected here.
        // This is to ensure that the application reacts to all state and state does not get out of sync.
        // When working in this hook please take the time to refactor, remove this comment and resolve the dependencies
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetchingSettings]);

    useDidMountEffect(() => {
        const settingsUpdate = {
            smartLists: smartLists.filter((smartList) => smartList.selected).map((smartList) => smartList.id),
        } as IHubspotSettings;

        contactTypeItems.forEach((contactType) => {
            switch (contactType.id) {
                case contactTypes.CUSTOMER:
                    settingsUpdate.syncCustomers = contactType.selected;
                    break;
                case contactTypes.SUPPLIER:
                    settingsUpdate.syncSuppliers = contactType.selected;
                    break;
                case contactTypes.OTHER:
                    settingsUpdate.syncOthers = contactType.selected;
                    break;
            }
        });

        dispatch(updateSettingsSuccess(settingsUpdate));
    }, [contactTypeItems, smartLists]);

    const onSaveClick = (): void => {
        props.onSaveClick(settings as unknown as Partial<IHubspotSettings>, true); // ISmartList are incompatible: casting as unknown since this is intentional.
    };

    const onCancelClick = (): void => {
        props.onCancelClick();
    };

    const onContactTypeSelect = (contactTypeId: string) => {
        setContactTypeItems(
            contactTypeItems.map((contactType) => {
                if (contactType.id === contactTypeId) {
                    contactType.selected = !contactType.selected;
                }

                return contactType;
            })
        );
    };

    const onSmartListSelect = (segmentationTagId: string) => {
        setSmartLists(
            smartLists.map((smartList) => {
                if (smartList.id === segmentationTagId) {
                    smartList.selected = !smartList.selected;
                }

                return smartList;
            })
        );
    };

    const segments = isFetchingSettings ? (
        <XUILoader ariaLabel='Loading' />
    ) : (
        <>
            <SetupSegment
                title={integrationLang.SETUP_PAGE.CONTACT_TYPES_TITLE}
                info={integrationLang.SETUP_PAGE.CONTACT_TYPES_INFORMATION}
                footer={integrationLang.SETUP_PAGE.CONTACT_TYPES_FOOTER}
            >
                <MultiselectPicklist
                    placeHolder={integrationLang.SETUP_PAGE.CONTACT_TYPES_PLACEHOLDER}
                    items={contactTypeItems}
                    onSelect={onContactTypeSelect}
                />
            </SetupSegment>
            {smartLists && smartLists.length ? (
                <SetupSegment
                    title={integrationLang.SETUP_PAGE.SEGMENTATION_TAGS_TITLE}
                    footer={integrationLang.SETUP_PAGE.SEGMENTATION_TAGS_FOOTER}
                >
                    <GroupedCheckbox items={smartLists} onSelect={onSmartListSelect} />
                </SetupSegment>
            ) : null}
        </>
    );

    return (
        <XUIPanel>
            <XUIPanelSection className='shopify-panel--section'>
                <div className='content xui-padding-bottom-3xlarge'>
                    <div className='xui-padding-large'>
                        <header className='setup--panel--header xui-margin-top-large xui-margin-bottom-5xlarge'>
                            <img src={logos.XERO} />
                            <span className='setup--panel--header--dashedhr' />
                            <img src={logos[installation.integrationType]} />
                        </header>
                        <div className='xui-margin-bottom-xlarge'>{integrationLang.SETUP_PAGE.BLURB}</div>
                        {segments}
                    </div>
                </div>
            </XUIPanelSection>
            <NavPanelSection
                onSubmitClick={onSaveClick}
                onCancelClick={onCancelClick}
                submitContent={installation.isOnboarded ? lang.BUTTONS.UPDATE : lang.BUTTONS.SYNC}
                cancelContent={lang.BUTTONS.CANCEL}
                fullWidth={true}
                isSaving={props.isSaving}
                isSubmitEnabled={contactTypeItems.some((contactTypeItem) => contactTypeItem.selected)}
            />
        </XUIPanel>
    );
};

export default HubspotSetupPanel;
